import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Chart from 'react-apexcharts';
import { motion } from 'framer-motion';
import { FaSearch, FaLightbulb, FaCalculator, FaChartLine } from 'react-icons/fa';

const MirrorEquationCalculator = () => {
  const [objectDistance, setObjectDistance] = useState('');
  const [focalLength, setFocalLength] = useState('');
  const [imageDistance, setImageDistance] = useState('');
  const [magnification, setMagnification] = useState('');
  const [showGraph, setShowGraph] = useState(false);

  const calculateImage = () => {
    if (objectDistance && focalLength) {
      const u = parseFloat(objectDistance);
      const f = parseFloat(focalLength);

      // Calculate image distance using mirror equation: 1/f = 1/v + 1/u
      const v = 1 / (1/f - 1/u);
      setImageDistance(v.toFixed(2));

      // Calculate magnification: m = -v/u
      const m = -v/u;
      setMagnification(m.toFixed(2));
      setShowGraph(true);
    }
  };

  const generateGraphData = () => {
    if (!objectDistance || !focalLength || !imageDistance || !magnification) return null;

    const u = parseFloat(objectDistance);
    const f = parseFloat(focalLength);
    const v = parseFloat(imageDistance);
    const m = parseFloat(magnification);

    // Generate mirror-style graph data
    const series = [
      {
        name: 'Object and Image Distances',
        data: Array.from({ length: 50 }, (_, i) => {
          const angle = (i / 50) * 2 * Math.PI;
          const radius = Math.abs(u) * (1 + Math.sin(angle) * 0.3);
          return {
            x: angle,
            y: radius
          };
        }),
      },
      {
        name: 'Magnification',
        data: Array.from({ length: 50 }, (_, i) => {
          const angle = (i / 50) * 2 * Math.PI;
          const radius = Math.abs(v) * (1 + Math.sin(angle + Math.PI) * 0.3);
          return {
            x: angle,
            y: radius
          };
        }),
      },
    ];

    const options = {
      chart: {
        type: 'line',
        height: 400,
        zoom: {
          enabled: false,
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: 'smooth',
        width: 3,
      },
      xaxis: {
        type: 'numeric',
        title: {
          text: 'Angle (rad)',
        },
        labels: {
          formatter: function (val) {
            return (val * 180 / Math.PI).toFixed(1);
          }
        }
      },
      yaxis: {
        title: {
          text: 'Distance (m)',
        },
        labels: {
          formatter: function (val) {
            return val.toFixed(2);
          }
        }
      },
      title: {
        text: 'Mirror Characteristics',
        align: 'center',
      },
      markers: {
        size: 0,
      },
      annotations: {
        yaxis: [
          {
            y: Math.abs(u),
            borderColor: '#FF4560',
            label: {
              borderColor: '#FF4560',
              style: {
                color: '#fff',
                background: '#FF4560',
              },
              text: 'Object Distance = ' + u.toFixed(2) + 'm'
            }
          },
          {
            y: Math.abs(v),
            borderColor: '#00E396',
            label: {
              borderColor: '#00E396',
              style: {
                color: '#fff',
                background: '#00E396',
              },
              text: 'Image Distance = ' + v.toFixed(2) + 'm'
            }
          }
        ]
      },
      tooltip: {
        y: {
          formatter: function (val) {
            return val.toFixed(2) + 'm';
          }
        }
      }
    };

    return { series, options };
  };

  return (
    <div className="min-h-screen bg-gradient-to-r from-blue-50 to-purple-50">
      <Helmet>
        <title>Mirror Equation Calculator | Physics Tool</title>
        <meta name="description" content="Calculate image distance and magnification using mirror equation. Interactive calculator with mirror-style visualization showing optical characteristics." />
        <meta name="keywords" content="mirror equation calculator, physics calculator, optics calculator, optical calculator" />
        <meta property="og:title" content="Mirror Equation Calculator | Physics Tool" />
        <meta property="og:description" content="Calculate image distance and magnification using mirror equation. Interactive calculator with mirror-style visualization showing optical characteristics." />
        <meta property="og:image" content="/social-preview.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Mirror Equation Calculator | Physics Tool" />
        <meta name="twitter:description" content="Calculate image distance and magnification using mirror equation. Interactive calculator with mirror-style visualization showing optical characteristics." />
        <link rel="canonical" href="https://yourdomain.com/physics-calculators/mirror-equation" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <div className="header-icon text-4xl text-blue-600 mb-4">
            <FaSearch />
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Mirror Equation Calculator</h1>
          <p className="text-xl text-gray-600">Calculate image distance and magnification using mirror equation</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold mb-4">Calculator</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Object Distance (u)</label>
                <input
                  type="number"
                  value={objectDistance}
                  onChange={(e) => setObjectDistance(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter object distance (m)"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Focal Length (f)</label>
                <input
                  type="number"
                  value={focalLength}
                  onChange={(e) => setFocalLength(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter focal length (m)"
                />
              </div>
              <div>
                <button
                  onClick={calculateImage}
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                >
                  Calculate Image
                </button>
              </div>

              {objectDistance && focalLength && imageDistance && magnification && (
                <div className="mt-4 p-4 bg-blue-100 rounded">
                  <div className="flex items-center">
                    <FaCalculator className="text-2xl text-blue-600 mr-2" />
                    <div>
                      <h3 className="text-lg font-bold text-blue-800">Results</h3>
                      <div className="mt-2 space-y-2">
                        <p className="text-blue-800">Object Distance = {objectDistance} m</p>
                        <p className="text-blue-800">Focal Length = {focalLength} m</p>
                        <p className="text-blue-800">Image Distance = {imageDistance} m</p>
                        <p className="text-blue-800">Magnification = {magnification}</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 p-4 bg-white rounded-lg shadow-md">
                    <div className="flex items-center mb-4">
                      <FaLightbulb className="text-yellow-500 mr-3" />
                      <h3 className="text-lg font-semibold">Solution Explanation</h3>
                    </div>
                    <p className="text-gray-600">
                      Using the mirror equation:
                      <br />
                      1/f = 1/v + 1/u
                      <br />
                      <br />
                      Where:
                      <br />
                      f = Focal Length = {focalLength} m
                      <br />
                      u = Object Distance = {objectDistance} m
                      <br />
                      v = Image Distance = {imageDistance} m
                      <br />
                      m = Magnification = {magnification}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {showGraph && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-bold mb-4">Mirror Characteristics</h2>
              <Chart
                options={generateGraphData().options}
                series={generateGraphData().series}
                type="line"
                height={400}
              />
              <p className="mt-4 text-gray-600 text-center">
                Mirror-style visualization showing optical characteristics
              </p>
            </div>
          )}
        </div>

        <div className="mt-12 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">How to Use</h2>
          <ol className="list-decimal list-inside space-y-2">
            <li>Enter the object distance (u)</li>
            <li>Enter the focal length (f)</li>
            <li>Click "Calculate Image" to get the results</li>
            <li>View the mirror-style visualization</li>
          </ol>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">Features & Benefits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="text-xl font-semibold mb-2">Mirror Characterization</h3>
              <p>Calculate image distance and magnification using mirror equation</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Interactive Visualization</h3>
              <p>Unique mirror-style graph showing optical characteristics</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Educational Tool</h3>
              <p>Ideal for optics and physics students</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Step-by-Step Solutions</h3>
              <p>See how calculations are performed</p>
            </div>
          </div>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">FAQs</h2>
          <div className="space-y-6">
            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What is Mirror Equation?</h3>
              <p className="text-gray-600">
                Mirror equation relates the focal length of a mirror to the object and image distances.
              </p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What units should I use?</h3>
              <p className="text-gray-600">
                For accurate calculations:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>All distances should be in meters (m)</li>
                  <li>The result will be in meters (m)</li>
                  <li>Magnification is unitless</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">How does the mirror-style graph work?</h3>
              <p className="text-gray-600">
                The mirror-style graph shows:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>The relationship between object and image distances</li>
                  <li>The spiral pattern represents the optical path</li>
                  <li>Helps visualize how different distances affect image formation</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What are some real-world applications?</h3>
              <p className="text-gray-600">
                Mirror calculations are important in many areas:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Mirror design and manufacturing</li>
                  <li>Optical systems</li>
                  <li>Telescopes and microscopes</li>
                  <li>Research and development</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">Why is this calculator useful?</h3>
              <p className="text-gray-600">
                This calculator helps:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Opticians design mirrors</li>
                  <li>Students understand optics principles</li>
                  <li>Teachers demonstrate physics concepts</li>
                  <li>Anyone working with mirrors</li>
                </ul>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MirrorEquationCalculator;
